import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router";
import { motion } from "motion/react";
import { ArrowLeft, Github, ExternalLink, ShieldCheck, Cpu, Code2, ArrowRight, RefreshCw } from "lucide-react";
import { ImageWithFallback } from "../components/figma/ImageWithFallback";
import { Section } from "../components/section";
import { SEO } from "../components/seo";
import { supabase } from "@/integrations/supabase/client";
import { Project } from "../components/projects";
import { initialProjects } from "../data/initial-data";

export function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState<Project | null>(null);
  const [nextProject, setNextProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProjectData = async () => {
      setIsLoading(true);
      
      try {
        // Try fetching from KV store
        const { data } = await supabase
          .from('kv_store_5f13ee94')
          .select('value')
          .eq('key', `project:${id}`)
          .maybeSingle();
        
        let currentProject = data?.value as Project;

        // Fallback to initial data if not in DB
        if (!currentProject) {
          currentProject = initialProjects.find(p => p.id === id) || null;
        }

        if (currentProject) {
          setProject(currentProject);
          
          // Find next project for navigation
          const { data: allData } = await supabase
            .from('kv_store_5f13ee94')
            .select('value')
            .like('key', 'project:%');
          
          let nextP = null;
          if (allData && allData.length > 1) {
            nextP = allData.find(d => d.value.id !== id)?.value;
          } else {
            nextP = initialProjects.find(p => p.id !== id);
          }
          setNextProject(nextP || null);
        }
      } catch (err) {
        console.error("Error fetching project detail:", err);
        const fallback = initialProjects.find(p => p.id === id);
        setProject(fallback || null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjectData();
  }, [id]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-violet-500 animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <>
        <SEO title="Project Not Found" noindex={true} />
        <div className="min-h-screen bg-black pt-32 pb-16 px-4 text-center">
          <h1 className="text-3xl font-black text-white mb-8 uppercase tracking-tighter">Project Not Found</h1>
          <Link to="/" className="text-neutral-500 hover:text-white flex items-center gap-2 justify-center font-bold uppercase tracking-widest text-xs transition-all">
            <ArrowLeft size={20} /> Back to Home
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO 
        title={project.title}
        description={project.full_description || project.description}
        keywords={`${project.title}, ${project.category}, web development project, ${project.tech_stack?.join(', ')}`}
        canonical={`/projects/${project.id}`}
        ogType="article"
      />
      <div className="pt-24 pb-16 bg-black min-h-screen">
        <Section id="project-hero" title={project.title} subtitle={project.category}>
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Link to="/" className="text-neutral-500 hover:text-white flex items-center gap-2 mb-12 font-bold uppercase tracking-widest text-xs transition-colors">
                <ArrowLeft size={16} /> All Projects
              </Link>
              
              <h3 className="text-2xl font-black mb-6 uppercase tracking-tight text-white">Technical Overview</h3>
              <p className="text-neutral-400 text-lg leading-relaxed mb-8">
                {project.full_description}
              </p>

              {project.challenges && project.challenges.length > 0 && (
                <div className="space-y-8 mb-12">
                   <h4 className="text-sm font-black uppercase tracking-[0.2em] text-neutral-500">Key Challenges</h4>
                   <div className="grid gap-4">
                     {project.challenges.map((challenge, i) => (
                       <div key={i} className="flex gap-4 items-start p-4 bg-neutral-900 border border-neutral-800 rounded-2xl">
                         <div className="bg-neutral-800 p-2 rounded-lg text-neutral-400">
                           <Cpu size={16} />
                         </div>
                         <p className="text-sm text-neutral-300 font-medium">{challenge}</p>
                       </div>
                     ))}
                   </div>
                </div>
              )}

              <div className="flex flex-wrap gap-4">
                 {project.link_url && (
                   <a 
                     href={project.link_url} 
                     target="_blank" 
                     rel="noreferrer"
                     className="bg-neutral-100 text-neutral-950 px-8 py-4 rounded-xl font-black uppercase tracking-tighter flex items-center gap-2 hover:bg-neutral-300 transition-colors shadow-lg"
                   >
                     Live Demo <ExternalLink size={18} />
                   </a>
                 )}
                 {project.github_url && (
                   <a 
                     href={project.github_url} 
                     target="_blank" 
                     rel="noreferrer"
                     className="border border-neutral-800 px-8 py-4 rounded-xl font-black uppercase tracking-tighter flex items-center gap-2 hover:bg-neutral-900 transition-colors text-white"
                   >
                     <Github size={18} /> Source Code
                   </a>
                 )}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="rounded-3xl overflow-hidden border border-neutral-800 shadow-2xl">
                <ImageWithFallback 
                  src={project.image_url} 
                  alt={project.title} 
                  className="w-full aspect-[4/3] object-cover" 
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-6 bg-neutral-900 border border-neutral-800 rounded-2xl">
                  <span className="text-[10px] font-black uppercase tracking-widest text-neutral-500 block mb-2">Framework</span>
                  <div className="flex items-center gap-2 text-neutral-100 font-bold">
                    <Code2 size={16} className="text-neutral-500" /> {project.tags?.[0] || 'React'}
                  </div>
                </div>
                <div className="p-6 bg-neutral-900 border border-neutral-800 rounded-2xl">
                  <span className="text-[10px] font-black uppercase tracking-widest text-neutral-500 block mb-2">Database</span>
                  <div className="flex items-center gap-2 text-neutral-100 font-bold">
                    <ShieldCheck size={16} className="text-neutral-500" /> {project.tags?.[2] || 'Supabase'}
                  </div>
                </div>
              </div>

              <div className="p-8 bg-neutral-900/50 border border-neutral-800 rounded-3xl">
                <h4 className="text-sm font-black uppercase tracking-widest text-neutral-500 mb-6">Stack & Tools</h4>
                <div className="flex flex-wrap gap-2">
                  {project.tags?.map(tag => (
                     <span key={tag} className="text-[10px] px-3 py-1.5 bg-neutral-950 border border-neutral-800 rounded-lg text-neutral-400 font-black uppercase tracking-tighter">
                       {tag}
                     </span>
                  ))}
                  {project.tech_stack?.map(tech => (
                     <span key={tech} className="text-[10px] px-3 py-1.5 bg-neutral-950 border border-neutral-800 rounded-lg text-neutral-400 font-black uppercase tracking-tighter">
                       {tech}
                     </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </Section>

        {nextProject && (
          <section className="py-20 px-6 border-t border-neutral-800/50">
            <div className="max-w-7xl mx-auto">
              <Link 
                to={`/projects/${nextProject.id}`}
                className="group block relative overflow-hidden rounded-[3rem] bg-neutral-900/40 border border-neutral-800 hover:border-violet-500/50 transition-all duration-700"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-violet-600/10 to-fuchsia-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="p-12 md:p-20 flex flex-col md:flex-row items-center justify-between gap-12 relative z-10">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-[0.4em] text-neutral-500 mb-4 block">Next Project</span>
                    <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter italic text-white group-hover:text-violet-400 transition-colors">
                      {nextProject.title}
                    </h2>
                  </div>
                  <div className="w-20 h-20 rounded-full border border-neutral-800 flex items-center justify-center group-hover:border-violet-500 group-hover:bg-violet-500 transition-all duration-500">
                    <ArrowRight size={32} className="text-neutral-500 group-hover:text-white transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            </div>
          </section>
        )}
      </div>
    </>
  );
}